/**
 * The intake link: what it has done, and how to replace it.
 *
 * The token is never returned by the server after it is minted — only its hash
 * is stored — so this dialog can show the link's history but not the link.
 * Rotating is how a designer cuts off one that went further than they meant,
 * and the new URL is legible exactly once, here.
 *
 * A new link takes an expiry and an optional password. The live link's expiry
 * can be moved without replacing it, which is how a lapsed link is reopened
 * for a late follow-up.
 */

import { useEffect, useState } from 'react';
import { ds } from '../ds';
import { api, ApiError } from '../api';
import { RADIUS, display, formatDate, formatWhen, monoSmall } from '../theme';

/** The terms on offer. The server accepts 1–180 days. */
const TERMS = [
  { value: '7', label: '7 days' },
  { value: '30', label: '30 days' },
  { value: '90', label: '90 days' },
  { value: '180', label: '180 days' }
];

interface Meta {
  viewCount: number;
  createdAt: number;
  expiresAt: number | null;
  hasPassword: boolean;
}

/** "Expires 6 Aug." or "Expired 6 Aug." */
const expiryLine = (expiresAt: number | null): string => {
  if (expiresAt === null) return '';
  const lapsed = expiresAt <= Date.now() / 1000;
  return ` ${lapsed ? 'Expired' : 'Expires'} ${formatDate(expiresAt)}.`;
};

interface Props {
  projectId: string;
  onClose: () => void;
  onRotated: () => Promise<void>;
}

export function ShareLink({ projectId, onClose, onRotated }: Props) {
  const { Button, Input, Select } = ds();
  const [fresh, setFresh] = useState<string | null>(null);
  const [meta, setMeta] = useState<Meta | null>(null);
  const [term, setTerm] = useState('30');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const onEscape = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onEscape);
    return () => document.removeEventListener('keydown', onEscape);
  }, [onClose]);

  useEffect(() => {
    void api.projects
      .detail(projectId)
      .then((d) => setMeta(d.link))
      .catch(() => {});
  }, [projectId]);

  const rotate = async () => {
    setError(null);
    try {
      const res = await api.projects.rotateLink(projectId, {
        expiresInDays: Number(term),
        ...(password ? { password } : {})
      });
      setFresh(`${window.location.origin}${res.url}`);
      setMeta({
        viewCount: 0,
        createdAt: Math.floor(Date.now() / 1000),
        expiresAt: res.expiresAt,
        hasPassword: !!password
      });
      await onRotated();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not make a new link.');
    }
  };

  const extend = async () => {
    setError(null);
    try {
      const { link } = await api.projects.setLinkExpiry(projectId, Number(term));
      setMeta((m) => (m ? { ...m, expiresAt: link.expiresAt } : m));
      await onRotated();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not change the expiry.');
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Intake link"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      className="mv-scrim"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 40,
        background: 'rgba(8,6,16,.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24
      }}
    >
      <div
        className="mv-pop"
        style={{
          background: 'var(--surface-card)',
          borderRadius: RADIUS.panel,
          padding: '32px 36px',
          width: 'min(560px, 100%)',
          boxShadow: '0 24px 60px rgba(8,6,16,.5)'
        }}
      >
        <div style={{ ...monoSmall, marginBottom: 10 }}>Intake link</div>
        <h2 style={display(26)}>{fresh ? 'Here is the new link.' : 'One live link per project.'}</h2>

        {fresh ? (
          <>
            <p style={{ margin: '10px 0 18px', fontSize: 14, lineHeight: 1.6, color: 'var(--text-muted)' }}>
              The old link stopped working. This one is shown once.{expiryLine(meta?.expiresAt ?? null)}
            </p>
            <div
              style={{
                display: 'flex',
                gap: 10,
                alignItems: 'center',
                padding: '14px 16px',
                background: 'var(--surface-inset)',
                borderRadius: RADIUS.control,
                flexWrap: 'wrap'
              }}
            >
              <code
                style={{
                  fontFamily: 'var(--font-mono), monospace',
                  fontSize: 13,
                  wordBreak: 'break-all',
                  flex: '1 1 240px',
                  color: 'var(--text-body)'
                }}
              >
                {fresh}
              </code>
              <Button
                variant="soft"
                size="sm"
                onClick={() => {
                  void navigator.clipboard?.writeText(fresh);
                  setCopied(true);
                }}
              >
                {copied ? 'Copied' : 'Copy link'}
              </Button>
            </div>
          </>
        ) : (
          <>
            <p style={{ margin: '10px 0 18px', fontSize: 14, lineHeight: 1.6, color: 'var(--text-muted)' }}>
              {meta
                ? `Made ${formatWhen(meta.createdAt)}, opened ${meta.viewCount} time${meta.viewCount === 1 ? '' : 's'}.` +
                  expiryLine(meta.expiresAt) +
                  (meta.hasPassword ? ' Password set.' : '') +
                  ' Making a new one stops the old.'
                : 'No live link. Make one to send.'}
            </p>
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div style={{ flex: '0 1 160px' }}>
                <Select
                  label="Expires after"
                  value={term}
                  options={TERMS}
                  onChange={(e) => setTerm(e.target.value)}
                />
              </div>
              <div style={{ flex: '1 1 220px' }}>
                <Input
                  label="Password for a new link"
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Optional, 8 or more characters"
                />
              </div>
            </div>
          </>
        )}

        {error && <p style={{ fontSize: 14, color: 'var(--danger-500)' }}>{error}</p>}

        <div style={{ display: 'flex', gap: 10, marginTop: 22, flexWrap: 'wrap' }}>
          {!fresh && (
            <Button variant="primary" size="md" onClick={() => void rotate()}>
              {meta ? 'Make a new link' : 'Make a link'}
            </Button>
          )}
          {!fresh && meta && (
            <Button variant="secondary" size="md" onClick={() => void extend()}>
              Set expiry
            </Button>
          )}
          <Button variant="ghost" size="md" onClick={onClose}>
            {fresh ? 'Done' : 'Close'}
          </Button>
        </div>
      </div>
    </div>
  );
}
